import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../../contexts/LanguageContext";
import { sendCustomerPass } from "../../api/mutations";
import { Modal } from "./ui";
import { buttonClass, inputClass, labelClass } from "./styles";

// Staff-facing re-send of the wallet pass by email. Email is optional at
// signup (stores may require phone instead), so when the customer has no
// email the staff supplies one here; when they do, we only confirm the
// address the pass will go to — it is never overwritten from this flow.
export default function SendPassModal({ isOpen, onClose, customer, storeId }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);

  const hasEmail = !!customer?.email;

  const sendPassMutation = useMutation({
    mutationFn: sendCustomerPass,
    onSuccess: (data) => {
      // Prefix match: CustomerShow keys the customer by whatever reference the
      // URL carried (uuid, or an old numeric id), not necessarily customer.id.
      queryClient.invalidateQueries({ queryKey: ["customer", storeId] });
      queryClient.invalidateQueries({ queryKey: ["customers", storeId] });
      setEmail("");
      setError(null);
      onClose({ sentTo: data.email });
    },
    onError: (err) => {
      setError(
        err.response?.data?.errors?.join(", ") || t("customers.sendPass.error"),
      );
    },
  });

  const handleSend = () => {
    if (!hasEmail && !email.trim()) {
      setError(t("customers.sendPass.emailRequired"));
      return;
    }

    sendPassMutation.mutate({
      storeId,
      customerId: customer.id,
      email: hasEmail ? undefined : email.trim(),
    });
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("customers.sendPass.title")}
      icon={
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-700">
          <EnvelopeIcon aria-hidden="true" className="h-5 w-5" />
        </span>
      }
      footer={
        <>
          <button type="button" onClick={handleClose} className={buttonClass.secondary}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sendPassMutation.isPending}
            className={buttonClass.primary}
          >
            {sendPassMutation.isPending ? t("customers.sendPass.sending") : t("customers.sendPass.send")}
          </button>
        </>
      }
    >
      {hasEmail ? (
        <p className="m-0 text-sm leading-relaxed text-gray-700">
          {t("customers.sendPass.confirmExisting", {
            email: <strong className="font-semibold text-gray-900">{customer.email}</strong>,
          })}
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          <p className="m-0 text-sm leading-relaxed text-gray-700">
            {t("customers.sendPass.emailPrompt")}
          </p>
          <label className={labelClass}>
            {t("customers.table.email")}
            <input
              type="email"
              name="send-email"
              autoComplete="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("customers.sendPass.emailPlaceholder")}
              aria-invalid={error ? "true" : "false"}
              className={inputClass}
            />
          </label>
        </div>
      )}
      {error && (
        <p role="alert" className="m-0 text-[13px] text-red-700">
          {error}
        </p>
      )}
    </Modal>
  );
}
